import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { JSX, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { logoutApiCall } from '../../../services/api/users';
import { APP_ROUTES } from '../../../utils/constants/appRoutes';
import { getRoleString } from '../../../utils/helpers/getRoleString';
import { removeToken } from '../../../utils/security/tokenStorage';
import ArrowDownLoginIcon from '../../Icons/ArrowDownLoginIcon';
import LogOutIcon from '../../Icons/LogOutIcon';
import { PasswordChangeModal } from '../../PasswordChangeModal';
import EnvironmentBadge from '../../common/EnvironmentBadge/EnvironmentBadge';

const Header = (): JSX.Element => {
  const {
    timezoneName,
    timezoneValue,
    Timezone,
    FirstName: firstName,
    LastName: lastName,
    ContactEmail: contactEmail,
    userType,
    AccessLevel: accessLevel,
  } = useSelector((state: any) => state.user.user);

  const role = getRoleString(accessLevel);
  const timezoneDisplay = timezoneName || timezoneValue || Timezone;
  const styleTypographyUser = {
    fontSize: '14px',
    lineHeight: '20px',
  };

  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClosePasswordModal = (): void => setShowPasswordModal(false);
  const handleShowPasswordModal = (): void => {
    setShowPasswordModal(true);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = (): void => {
    setIsDropdownOpen(false);

    logoutApiCall()
      .then(() => {
        removeToken();
        redirectAfterLogout();
      })
      .catch(error => {
        console.log(error);
        removeToken();
        redirectAfterLogout();
      });
  };

  const redirectAfterLogout = (): void => {
    if (userType === 'azure') {
      window.location.href = `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${window.location.origin}`;
    } else {
      window.location.href = '/';
    }
  };

  return (
    <>
      <AppBar
        color="inherit"
        position="sticky"
        sx={{
          boxShadow: 'none',
          backgroundColor: '#F8F9FA',
          borderBottom: '1px solid #DEE2E6',
          padding: '8px 24px 8px 16px',
          minHeight: '40px',
          zIndex: '20',
        }}
      >
        <Toolbar
          sx={{
            minHeight: 'inherit !important',
            padding: '0 !important',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-end', sm: 'center' },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
            <Typography sx={{ color: '#327BBE', fontWeight: '700' }}>
              Timezone: {timezoneDisplay}
            </Typography>
            <EnvironmentBadge />
          </Box>
          <Box sx={{ position: 'relative' }} ref={dropdownRef}>
            <Box
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ color: '#000', ...styleTypographyUser }}>{firstName}</Typography>
                <Typography sx={{ color: 'rgba(0, 0, 0, 0.38)', ...styleTypographyUser }}>
                  {role}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="initial-circle">{firstName ? firstName[0].toUpperCase() : ''}</div>
                <ArrowDownLoginIcon />
              </Box>
            </Box>
            {isDropdownOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  right: '0',
                  top: '100%',
                  width: 'auto',
                  height: 'auto',
                  backgroundColor: '#fff',
                  whiteSpace: 'nowrap',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '4px',
                  marginTop: '8px',
                  paddingTop: '8px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Grid
                  container
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '12px',
                  }}
                >
                  <Grid item xs={12} sx={{ padding: '6px 12px' }}>
                    <Typography sx={{ color: '#000', ...styleTypographyUser }}>
                      User:{' '}
                      <strong style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                        {firstName} {lastName}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ padding: '6px 12px' }}>
                    <Typography sx={{ color: '#000', ...styleTypographyUser }}>
                      Email:{' '}
                      <strong style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{contactEmail}</strong>
                    </Typography>
                  </Grid>
                  {userType === 'local' && (
                    <Grid
                      item
                      xs={12}
                      onClick={handleShowPasswordModal}
                      sx={{
                        cursor: 'pointer',
                        ':hover': { background: '#F0F8FF', color: '#05629F' },
                        padding: '6px 12px',
                      }}
                    >
                      <Typography sx={{ cursor: 'pointer', ...styleTypographyUser }}>
                        Change Password
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sx={{ padding: 0 }}>
                    <Link
                      to={APP_ROUTES.ACCOUNT_SECURITY}
                      onClick={() => setIsDropdownOpen(false)}
                      style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}
                    >
                      <Box
                        sx={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          ':hover': { background: '#F0F8FF', color: '#05629F' },
                        }}
                      >
                        <Typography sx={{ ...styleTypographyUser }}>Two-step verification</Typography>
                      </Box>
                    </Link>
                  </Grid>
                </Grid>
                <Divider />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    width: '100%',
                    cursor: 'pointer',
                    ':hover': { background: '#F0F8FF', color: '#05629F' },
                  }}
                  onClick={handleLogout}
                >
                  <Typography sx={styleTypographyUser}>Logout&nbsp;</Typography>
                  <LogOutIcon />
                </Box>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <PasswordChangeModal
        showPasswordModal={showPasswordModal}
        handleClosePasswordModal={handleClosePasswordModal}
        showConfirmation={showConfirmation}
        setShowConfirmation={setShowConfirmation}
      />
    </>
  );
};

export default Header;
